// CRM 2.0 — Analytics presentation constants + honest formatting helpers.

import type { BadgeTone } from '@/shared/components/ui/Badge';
import { localToday } from '@/shared/utils/utils';

/** Human labels for the six canonical Deal_Stages (mirrors DEAL_STAGES). */
export const STAGE_LABELS: Record<string, string> = {
  prospecting: 'Prospecting',
  qualification: 'Qualification',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

/** Badge tone per stage, so open/won/lost read at a glance. */
export const STAGE_TONE: Record<string, BadgeTone> = {
  prospecting: 'neutral',
  qualification: 'info',
  proposal: 'info',
  negotiation: 'warning',
  closed_won: 'success',
  closed_lost: 'danger',
};

/** Human labels for the funnel Lead statuses (mirrors the leadSchema enum). */
export const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  interested: 'Interested',
  converted: 'Converted',
  rejected: 'Rejected',
};

export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? stage;
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/**
 * Format a single-currency money total. Uses `Intl.NumberFormat` with the
 * currency reported by the server; falls back to a code-prefixed number for any
 * currency `Intl` cannot resolve. NEVER combines currencies — each
 * `CurrencyTotal` is formatted on its own (Req 1.3).
 */
export function formatCurrency(total: number, currency: string): string {
  const code = (currency || 'INR').toUpperCase();
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(total || 0);
  } catch {
    // Unknown/invalid ISO currency code → still render honestly, code-prefixed.
    return `${code} ${Number(total || 0).toLocaleString('en-IN')}`;
  }
}

/** Plain integer formatting for counts. */
export function formatCount(n: number): string {
  return Number(n || 0).toLocaleString('en-IN');
}

/** Round seconds to a compact `Xm Ys` / `Ys` label for call durations (Req 5.2). */
export function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

/** Default reporting window for date-range views: the trailing 30 days. */
export function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function todayISO(): string {
  return localToday();
}

/** Page size for the pipeline stage-detail list (server caps at 500). */
export const STAGE_DETAIL_PAGE = 25;
